import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatRp } from "@/lib/tiers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Edit, Trash2, Plus, History, Search } from "lucide-react";
import Avatar from "@/components/Avatar";

const SOURCES = [
  { value: "saweria", label: "Saweria" },
  { value: "discord", label: "Discord" },
  { value: "direct_transfer", label: "Direct Transfer" },
  { value: "manual", label: "Manual" },
  { value: "other", label: "Other" },
];

const sourceLabel = (s) => SOURCES.find((x) => x.value === s)?.label || s;

const todayIsoDate = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const toDateInputValue = (iso) => {
  if (!iso) return todayIsoDate();
  try {
    const d = new Date(iso);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return todayIsoDate();
  }
};

export default function AdminContributions() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [sumAmount, setSumAmount] = useState(0);
  const [q, setQ] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // contribution or "new"
  const [auditFor, setAuditFor] = useState(null);
  const [audit, setAudit] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q) params.q = q;
      if (sourceFilter) params.source = sourceFilter;
      const { data } = await api.get("/admin/contributions", { params });
      setItems(data.contributions || []);
      setTotal(data.total || 0);
      setSumAmount(data.sum_amount || 0);
    } catch (e) {
      console.error("Failed to load contributions:", e);
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [q, sourceFilter]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const handleSave = async (payload, id) => {
    try {
      if (id) {
        await api.put(`/admin/contributions/${id}`, payload);
        toast.success("Contribution updated");
      } else {
        await api.post("/admin/contributions", payload);
        toast.success("Contribution added");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete ${formatRp(c.amount)} contribution by ${c.user_name || c.user_email}?`)) return;
    try {
      await api.delete(`/admin/contributions/${c.id}`);
      toast.success("Contribution deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const openAudit = async (c) => {
    setAuditFor(c);
    try {
      const { data } = await api.get(`/admin/contributions/${c.id}/audit`);
      setAudit(data.audit || []);
    } catch (e) {
      setAudit([]);
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load audit");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display text-xl font-bold text-white">Supporter Contributions</h2>
        <Button
          onClick={() => setEditing("new")}
          className="bg-rose-600 hover:bg-rose-500"
          data-testid="contrib-add-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Contribution
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="glass rounded-lg p-3">
          <div className="text-xs text-zinc-500 uppercase tracking-wider">Entries</div>
          <div className="text-white text-xl font-bold mt-1" data-testid="contrib-stat-count">{total}</div>
        </div>
        <div className="glass rounded-lg p-3">
          <div className="text-xs text-zinc-500 uppercase tracking-wider">Sum (current filter)</div>
          <div className="text-amber-300 text-xl font-bold mt-1" data-testid="contrib-stat-sum">{formatRp(sumAmount)}</div>
        </div>
        <div className="glass rounded-lg p-3 flex items-center gap-2">
          <Search className="w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search user / notes…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-transparent border-0 focus-visible:ring-0 px-0 text-white"
            data-testid="contrib-search"
          />
          <Select value={sourceFilter || "all"} onValueChange={(v) => setSourceFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-32 bg-black/40 border-white/10 text-zinc-200" data-testid="contrib-source-filter">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-white/10 text-white">
              <SelectItem value="all">All</SelectItem>
              {SOURCES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="glass rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-zinc-400 text-xs uppercase tracking-wider bg-black/40">
              <tr>
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Amount</th>
                <th className="text-left p-3">Source</th>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Added By</th>
                <th className="text-left p-3">Notes</th>
                <th className="text-left p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-white/5" data-testid={`contrib-row-${c.id}`}>
                  <td className="p-3 text-white">
                    <div className="flex items-center gap-2">
                      <Avatar user={{ name: c.user_name, avatar_url: "" }} size={26} />
                      <div>
                        <div className="text-sm">{c.user_name || "—"}</div>
                        <div className="text-xs text-zinc-500">{c.user_email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-amber-300 font-semibold">{formatRp(c.amount)}</td>
                  <td className="p-3">
                    <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-200 border border-white/10">
                      {sourceLabel(c.source)}
                    </span>
                  </td>
                  <td className="p-3 text-zinc-300">
                    {c.contribution_date ? new Date(c.contribution_date).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-3 text-zinc-400 text-xs">{c.admin_name || "—"}</td>
                  <td className="p-3 text-zinc-400 text-xs max-w-[18ch] truncate" title={c.notes}>{c.notes || "—"}</td>
                  <td className="p-3">
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => setEditing(c)} data-testid={`contrib-edit-${c.id}`}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openAudit(c)} data-testid={`contrib-audit-${c.id}`}>
                        <History className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(c)}
                        className="border-rose-500/40 text-rose-300"
                        data-testid={`contrib-delete-${c.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && !loading && (
                <tr><td colSpan={7} className="text-center p-8 text-zinc-500">No contributions yet.</td></tr>
              )}
              {loading && (
                <tr><td colSpan={7} className="text-center p-8 text-zinc-500">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <EditDialog
        open={!!editing}
        contribution={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSave={handleSave}
      />

      <AuditDialog
        open={!!auditFor}
        contribution={auditFor}
        audit={audit}
        onClose={() => { setAuditFor(null); setAudit([]); }}
      />
    </div>
  );
}

function EditDialog({ open, contribution, onClose, onSave }) {
  const isNew = !contribution;
  const [userId, setUserId] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState("");
  const [source, setSource] = useState("saweria");
  const [notes, setNotes] = useState("");
  const [contribDate, setContribDate] = useState(todayIsoDate());

  useEffect(() => {
    if (!open) return;
    if (isNew) {
      setUserId(""); setSelectedUser(null); setUserSearch("");
      setAmount(""); setSource("saweria"); setNotes("");
      setContribDate(todayIsoDate());
    } else {
      setUserId(contribution.user_id);
      setSelectedUser({ id: contribution.user_id, name: contribution.user_name, email: contribution.user_email });
      setUserSearch("");
      setAmount(String(contribution.amount || ""));
      setSource(contribution.source || "saweria");
      setNotes(contribution.notes || "");
      setContribDate(toDateInputValue(contribution.contribution_date));
    }
  }, [open, contribution, isNew]);

  // user search (only for new)
  useEffect(() => {
    if (!isNew || !open) return;
    const term = userSearch.trim();
    if (term.length < 2) { setUserResults([]); return; }
    const handler = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/users", { params: { q: term, limit: 8 } });
        setUserResults(data.users || []);
      } catch {
        setUserResults([]);
      }
    }, 250);
    return () => clearTimeout(handler);
  }, [userSearch, isNew, open]);

  const canSave = useMemo(() => {
    return userId && amount && parseInt(amount, 10) > 0 && source && contribDate;
  }, [userId, amount, source, contribDate]);

  const handleSubmit = async () => {
    const payload = {
      user_id: userId,
      amount: parseInt(amount, 10),
      source,
      notes: notes || "",
      contribution_date: contribDate,
    };
    if (!isNew) {
      // Update payload doesn't include user_id (immutable)
      const updatePayload = {
        amount: payload.amount,
        source: payload.source,
        notes: payload.notes,
        contribution_date: payload.contribution_date,
      };
      await onSave(updatePayload, contribution.id);
    } else {
      await onSave(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add Contribution" : "Edit Contribution"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {isNew ? (
            <div>
              <Label className="text-xs text-zinc-400">User</Label>
              {selectedUser ? (
                <div className="mt-1 flex items-center justify-between gap-2 p-2 rounded bg-black/40 border border-white/10">
                  <div className="flex items-center gap-2">
                    <Avatar user={selectedUser} size={26} />
                    <div className="text-sm">
                      <div className="text-white">{selectedUser.name}</div>
                      <div className="text-xs text-zinc-500">{selectedUser.email}</div>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { setSelectedUser(null); setUserId(""); }} data-testid="contrib-user-clear">Change</Button>
                </div>
              ) : (
                <>
                  <Input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Type at least 2 letters of name or email…"
                    className="mt-1 bg-black/40 border-white/10"
                    data-testid="contrib-user-search"
                  />
                  {userResults.length > 0 && (
                    <div className="mt-1 max-h-48 overflow-y-auto rounded border border-white/10 bg-black/60">
                      {userResults.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => { setUserId(u.id); setSelectedUser(u); setUserResults([]); setUserSearch(""); }}
                          className="w-full text-left flex items-center gap-2 p-2 hover:bg-white/5"
                          data-testid={`contrib-user-pick-${u.id}`}
                        >
                          <Avatar user={u} size={24} />
                          <div className="text-sm">
                            <div className="text-white">{u.name}</div>
                            <div className="text-xs text-zinc-500">{u.email}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="p-2 rounded bg-black/40 border border-white/10 flex items-center gap-2">
              <Avatar user={{ name: contribution?.user_name }} size={26} />
              <div className="text-sm">
                <div className="text-white">{contribution?.user_name}</div>
                <div className="text-xs text-zinc-500">{contribution?.user_email}</div>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs text-zinc-400">Amount (IDR)</Label>
            <Input
              type="number"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 bg-black/40 border-white/10"
              placeholder="50000"
              data-testid="contrib-amount-input"
            />
          </div>

          <div>
            <Label className="text-xs text-zinc-400">Source</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="mt-1 bg-black/40 border-white/10" data-testid="contrib-source-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-white/10 text-white">
                {SOURCES.map((s) => (
                  <SelectItem key={s.value} value={s.value} data-testid={`contrib-source-opt-${s.value}`}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-zinc-400">Contribution Date</Label>
            <Input
              type="date"
              value={contribDate}
              onChange={(e) => setContribDate(e.target.value)}
              className="mt-1 bg-black/40 border-white/10"
              data-testid="contrib-date-input"
            />
          </div>

          <div>
            <Label className="text-xs text-zinc-400">Notes (optional)</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 bg-black/40 border-white/10"
              data-testid="contrib-notes-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="contrib-cancel-btn">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSave}
            className="bg-rose-600 hover:bg-rose-500"
            data-testid="contrib-save-btn"
          >
            {isNew ? "Add" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuditDialog({ open, contribution, audit, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>Audit Log</DialogTitle>
        </DialogHeader>
        {contribution && (
          <div className="text-xs text-zinc-400 mb-2">
            For: <span className="text-white">{contribution.user_name}</span> · {formatRp(contribution.amount)} · {sourceLabel(contribution.source)}
          </div>
        )}
        <div className="max-h-96 overflow-y-auto space-y-2" data-testid="contrib-audit-list">
          {audit.length === 0 && <div className="text-sm text-zinc-500">No audit entries.</div>}
          {audit.map((a) => (
            <div key={a.id} className="p-3 rounded bg-black/40 border border-white/10 text-xs">
              <div className="flex items-center justify-between">
                <span className={`px-2 py-0.5 rounded uppercase tracking-wider font-bold text-[10px] ${
                  a.action === "create" ? "bg-emerald-900/50 text-emerald-200 border border-emerald-500/30"
                  : a.action === "update" ? "bg-amber-900/50 text-amber-200 border border-amber-500/30"
                  : "bg-rose-900/50 text-rose-200 border border-rose-500/30"
                }`}>
                  {a.action}
                </span>
                <span className="text-zinc-500">{new Date(a.timestamp).toLocaleString()}</span>
              </div>
              <div className="mt-1 text-zinc-300">By <span className="text-white">{a.admin_name}</span></div>
              {a.action === "update" && a.before && a.after && (
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                  <Diff label="Amount" before={a.before.amount} after={a.after.amount} />
                  <Diff label="Source" before={a.before.source} after={a.after.source} />
                  <Diff label="Date" before={a.before.contribution_date} after={a.after.contribution_date} />
                  <Diff label="Notes" before={a.before.notes} after={a.after.notes} />
                </div>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="contrib-audit-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Diff({ label, before, after }) {
  if (before === after) return null;
  return (
    <div className="col-span-2">
      <div className="text-zinc-500">{label}</div>
      <div className="text-rose-300 line-through truncate">{String(before ?? "—")}</div>
      <div className="text-emerald-300 truncate">{String(after ?? "—")}</div>
    </div>
  );
}
